import { getBackendUrl } from '../config/api';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Users,
  BookOpen,
  GraduationCap,
  Building,
  Clock,
  CheckCircle,
  AlertTriangle,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { value: 'Monday', label: 'Monday' },
  { value: 'Tuesday', label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday', label: 'Thursday' },
  { value: 'Friday', label: 'Friday' },
  { value: 'Saturday', label: 'Saturday' }
];

const TIME_SLOTS = [
  '09:00-10:30',
  '10:30-12:00', 
  '12:00-13:30',
  '13:30-15:00',
  '15:00-16:30',
  '16:30-18:00'
];

const SmartTimetableBuilder = () => {
  const { user, isAdmin, isTeacher } = useAuth();
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [currentTeacher, setCurrentTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    batch_id: undefined,
    subject_id: undefined,
    teacher_id: undefined,
    classroom_id: undefined,
    total_classes: '',
    days_of_week: [],
    time_slot: undefined,
    start_date: ''
  });
  
  const [conflicts, setConflicts] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [conflictDialog, setConflictDialog] = useState({ 
    open: false, 
    message: '', 
    pendingData: null 
  });

  useEffect(() => {
    Promise.all([
      fetchBatches(),
      fetchSubjects(),
      fetchTeachers(),
      fetchClassrooms(),
      fetchTimeSlots(),
      ...(isTeacher() ? [fetchCurrentTeacher()] : [])
    ]).finally(() => setLoading(false));
  }, [user]);

  const fetchBatches = async () => {
    try {
      // Exclude archived batches from Smart Builder
      const response = await axios.get(`${API}/batches`, {
        params: { include_archived: false }
      });
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get(`${API}/classrooms`);
      setClassrooms(response.data);
    } catch (error) {
      console.error('Error fetching classrooms:', error);
    }
  };

  const fetchCurrentTeacher = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      // Find teacher by current user's email
      const teacherData = response.data.find(teacher => teacher.email === user.email);
      if (teacherData) {
        setCurrentTeacher(teacherData);
      }
    } catch (error) {
      console.error('Error fetching current teacher:', error);
    }
  };


  const fetchTimeSlots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/time-slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Combine default slots with custom slots
      const defaultSlots = [
        '09:00-10:30',
        '10:30-12:00', 
        '12:00-13:30',
        '13:30-15:00',
        '15:00-16:30',
        '16:30-18:00'
      ];
      
      // Convert custom slots to time slot format (e.g., "14:00-15:00")
      const customSlots = response.data.map(slot => slot.name || `${slot.start_time}-${slot.end_time}`);
      
      // Combine and remove duplicates
      const allSlots = [...defaultSlots, ...customSlots];
      const uniqueSlots = [...new Set(allSlots)];
      
      console.log('[SMART BUILDER] Time slots loaded:', uniqueSlots);
      setTimeSlots(uniqueSlots);
    } catch (error) {
      console.error('[SMART BUILDER] Error fetching time slots:', error);
      // Fallback to default slots only
      const defaultSlots = [
        '09:00-10:30',
        '10:30-12:00', 
        '12:00-13:30',
        '13:30-15:00',
        '15:00-16:30',
        '16:30-18:00'
      ];
      setTimeSlots(defaultSlots);
    }
  };


  const handleDayToggle = (day) => {
    const updatedDays = formData.days_of_week.includes(day)
      ? formData.days_of_week.filter(d => d !== day)
      : [...formData.days_of_week, day];
    
    setFormData({ ...formData, days_of_week: updatedDays });
  };

  const calculateSessionPreview = () => {
    if (!formData.total_classes || !formData.days_of_week.length || !formData.start_date) {
      return null;
    }

    const totalClasses = parseInt(formData.total_classes);
    const daysPerWeek = formData.days_of_week.length;
    const approximateWeeks = Math.ceil(totalClasses / daysPerWeek);
    
    return {
      totalClasses,
      daysPerWeek,
      approximateWeeks,
      sessionsPerWeek: daysPerWeek
    };
  };

  const handlePreview = () => {
    const preview = calculateSessionPreview();
    setPreviewData(preview);
    
    // Here you could also check for conflicts
    // checkForConflicts();
  };

  const handleSubmit = async (e, overrideConflicts = false) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      
      const courseData = {
        ...formData,
        total_classes: parseInt(formData.total_classes),
        override_conflicts: overrideConflicts
      };
      
      const response = await axios.post(`${API}/course-assignments`, courseData);
      
      toast.success(`Course assignment created! ${response.data.total_classes} sessions will be automatically scheduled.`);
      
      // Reset form and close any dialogs
      setFormData({
        batch_id: undefined,
        subject_id: undefined,
        teacher_id: undefined,
        classroom_id: undefined,
        total_classes: '',
        days_of_week: [],
        time_slot: undefined,
        start_date: ''
      });
      setPreviewData(null);
      setConflictDialog({ open: false, message: '', pendingData: null });
      
    } catch (error) {
      console.error('Error creating course assignment:', error);
      
      // Check if this is a conflict error (409 status)
      if (error.response?.status === 409 && error.response?.data?.detail?.can_override && !overrideConflicts) {
        // Show conflict confirmation dialog
        const conflictMessage = error.response.data.detail.message || 'A scheduling conflict was detected.';
        setConflictDialog({
          open: true,
          message: conflictMessage,
          pendingData: {
            ...formData,
            total_classes: parseInt(formData.total_classes)
          }
        });
      } else {
        // Handle other errors
        const errorMessage = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail
          : error.response?.data?.detail?.message || 'Failed to create course assignment';
        toast.error(`Error: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmConflict = async () => {
    // Retry creation with override_conflicts = true
    await handleSubmit(null, true);
  };

  const handleCancelConflict = () => {
    setConflictDialog({ open: false, message: '', pendingData: null });
  };

  const getBatchName = (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    return batch ? batch.name : 'Unknown Batch';
  };

  const getSelectedBatch = () => {
    return batches.find(b => b.id === formData.batch_id);
  };

  // Memoize filtered subjects and add dependency on currentTeacher
  const getFilteredSubjects = useMemo(() => {
    const selectedBatch = getSelectedBatch();
    if (!selectedBatch) return [];
    
    console.log('[SUBJECT FILTERING] Selected Batch:', {
      name: selectedBatch.name,
      stream: selectedBatch.stream,
      year: selectedBatch.year,
      semester: selectedBatch.semester
    });
    
    // If teacher mode and teacher data not loaded yet, return empty array
    if (isTeacher() && !currentTeacher && !loading) {
      return [];
    }
    
    // Parse batch semesters - batch might have comma-separated semesters
    let batchSemesters = [];
    if (selectedBatch.semester) {
      if (Array.isArray(selectedBatch.semester)) {
        batchSemesters = selectedBatch.semester;
      } else if (typeof selectedBatch.semester === 'string') {
        // Split by comma and trim whitespace
        batchSemesters = selectedBatch.semester.split(',').map(s => s.trim());
      }
    }
    
    console.log('[SUBJECT FILTERING] Batch semesters parsed:', batchSemesters);
    
    // Log initial subject count
    console.log('[SUBJECT FILTERING] Total subjects available:', subjects.length);
    console.log('[SUBJECT FILTERING] Is teacher mode:', isTeacher());
    if (isTeacher() && currentTeacher) {
      console.log('[SUBJECT FILTERING] Current teacher:', currentTeacher.name);
      console.log('[SUBJECT FILTERING] Teacher assigned subjects count:', currentTeacher.subjects?.length || 0);
    }
    
    const filtered = subjects.filter(subject => {
      // Debug log for each subject being checked
      const debugInfo = {
        subject_name: subject.name,
        subject_stream: subject.stream,
        subject_year: subject.year,
        subject_semester: subject.semester,
        batch_stream: selectedBatch.stream,
        batch_year: selectedBatch.year,
        batch_semester: selectedBatch.semester
      };
      
      // Filter by stream if batch has stream
      if (selectedBatch.stream && subject.stream !== selectedBatch.stream) {
        console.log('[SUBJECT FILTERING] ❌ Stream mismatch:', debugInfo);
        return false;
      }
      
      // Filter by year if batch has year
      if (selectedBatch.year && subject.year !== selectedBatch.year) {
        console.log('[SUBJECT FILTERING] ❌ Year mismatch:', debugInfo);
        return false;
      }
      
      // Filter by semester - handle multiple semesters in both batch and subject
      if (batchSemesters.length > 0 && subject.semester) {
        // Parse subject semesters - subject might also have comma-separated semesters
        let subjectSemesters = [];
        if (Array.isArray(subject.semester)) {
          subjectSemesters = subject.semester;
        } else if (typeof subject.semester === 'string') {
          // Split by comma and trim whitespace
          subjectSemesters = subject.semester.split(',').map(s => s.trim());
        }
        
        // Check if there's ANY overlap between batch semesters and subject semesters
        const hasOverlap = subjectSemesters.some(subjectSem => 
          batchSemesters.includes(subjectSem)
        );
        
        if (!hasOverlap) {
          console.log('[SUBJECT FILTERING] ❌ Semester mismatch - No overlap:', {
            subject_name: subject.name,
            subject_semesters: subjectSemesters,
            batch_semesters: batchSemesters
          });
          return false;
        }
      }
      
      // For teachers: only show subjects they are assigned to
      if (isTeacher() && currentTeacher) {
        console.log('[TEACHER FILTER] Teacher:', currentTeacher.name);
        console.log('[TEACHER FILTER] Teacher assigned subjects:', currentTeacher.subjects);
        console.log('[TEACHER FILTER] Checking subject:', subject.id, subject.name);
        
        if (!currentTeacher.subjects || !currentTeacher.subjects.includes(subject.id)) {
          console.log('[TEACHER FILTER] Subject NOT assigned to teacher, filtering out');
          return false;
        }
        console.log('[TEACHER FILTER] Subject IS assigned to teacher, keeping it');
      }
      
      console.log('[SUBJECT FILTERING] ✅ Subject passed all filters:', subject.name);
      return true;
    });
    
    console.log('[SUBJECT FILTERING] ============================================');
    console.log('[SUBJECT FILTERING] SUMMARY:');
    console.log('[SUBJECT FILTERING] - Batch:', selectedBatch.name);
    console.log('[SUBJECT FILTERING] - Total subjects available:', subjects.length);
    console.log('[SUBJECT FILTERING] - Filtered subjects count:', filtered.length);
    console.log('[SUBJECT FILTERING] ============================================');
    
    if (filtered.length === 0) {
      console.warn('[SUBJECT FILTERING] ⚠️ NO SUBJECTS MATCHED! Possible reasons:');
      console.warn('[SUBJECT FILTERING] 1. Stream mismatch - Batch stream:', selectedBatch.stream);
      console.warn('[SUBJECT FILTERING] 2. Year mismatch - Batch year:', selectedBatch.year);
      console.warn('[SUBJECT FILTERING] 3. Semester mismatch - Batch semesters:', batchSemesters);
      console.warn('[SUBJECT FILTERING] 4. Check if subjects exist with matching stream/year/semester');
    }
    
    if (filtered.length > 0) {
      console.log('[SUBJECT FILTERING] Sample filtered subjects:', filtered.slice(0, 3).map(s => ({
        name: s.name,
        stream: s.stream,
        year: s.year,
        semester: s.semester
      })));
    }
    
    return filtered;
  }, [subjects, formData.batch_id, currentTeacher, isTeacher, loading, batches]);

  const getGroupedSubjects = () => {
    const filteredSubjects = getFilteredSubjects;
    const grouped = {};

    filteredSubjects.forEach(subject => {
      const stream = subject.stream || 'Other';
      const year = subject.year || 'Other';
      const semester = subject.semester || 'Other';
      
      if (!grouped[stream]) {
        grouped[stream] = {};
      }
      if (!grouped[stream][year]) {
        grouped[stream][year] = {};
      }
      if (!grouped[stream][year][semester]) {
        grouped[stream][year][semester] = [];
      }
      
      grouped[stream][year][semester].push(subject);
    });

    // Sort subjects within each group
    Object.keys(grouped).forEach(stream => {
      Object.keys(grouped[stream]).forEach(year => {
        Object.keys(grouped[stream][year]).forEach(semester => {
          grouped[stream][year][semester].sort((a, b) => a.name.localeCompare(b.name));
        });
      });
    });

    return grouped;
  };

  const renderSubjectGroups = () => {
    const groupedSubjects = getGroupedSubjects();
    const elements = [];

    Object.keys(groupedSubjects).sort().forEach(stream => {
      // Stream header
      elements.push(
        <div key={`stream-${stream}`} className="px-2 py-2 text-xs font-bold text-gray-500 uppercase tracking-wide border-b">
          {stream}
        </div>
      );

      // Skill Enhancement: flat list, no year/semester grouping
      if (stream === 'Skill Enhancement') {
        const allSubjects = [];
        Object.keys(groupedSubjects[stream]).forEach(y => {
          Object.keys(groupedSubjects[stream][y]).forEach(s => {
            allSubjects.push(...groupedSubjects[stream][y][s]);
          });
        });
        allSubjects.sort((a, b) => a.name.localeCompare(b.name)).forEach(subject => {
          const selectedBatch = batches.find(b => b.id === formData.batch_id);
          const isCompleted = selectedBatch?.completed_subjects?.includes(subject.id);
          elements.push(
            <SelectItem key={subject.id} value={subject.id} className="pl-6">
              <div className="flex items-center justify-between w-full">
                <span className={isCompleted ? 'text-green-600 line-through' : ''}>
                  {subject.name}
                  {isCompleted && <span className="ml-2 text-xs">Completed</span>}
                </span>
                {subject.total_classes && (
                  <span className="text-xs text-gray-500 ml-2">({subject.total_classes} classes)</span>
                )}
              </div>
            </SelectItem>
          );
        });
        return;
      }

      // Define proper year order for each stream
      const getYearOrder = (stream) => {
        if (stream === 'MBA Design Management') {
          return ['PG First Year', 'PG Second Year', 'PG FY', 'PG SY', 'Year 1', 'Year 2']; // Support both formats
        } else {
          return ['FY', 'SY', 'TY', 'Fourth Year', 'PG First Year', 'PG Second Year', 'PG FY', 'PG SY'];
        }
      };

      // Define proper semester order for each year
      const getSemesterOrder = (year, stream) => {
        if (stream === 'MBA Design Management') {
          // MBA is postgraduate, so uses PG Sem naming
          if (year === 'PG FY' || year === 'PG First Year' || year === 'Year 1') return ['PG Sem 1', 'PG Sem 2'];
          if (year === 'PG SY' || year === 'PG Second Year' || year === 'Year 2') return ['PG Sem 3', 'PG Sem 4'];
        } else {
          // For Fashion Design and Interior Design
          if (year === 'FY') return ['Semester 1', 'Semester 2'];
          if (year === 'SY') return ['Semester 3', 'Semester 4'];
          if (year === 'TY') return ['Semester 5', 'Semester 6'];
          if (year === 'Fourth Year') return ['Semester 7', 'Semester 8'];
          if (year === 'PG FY' || year === 'PG First Year') return ['PG Sem 1', 'PG Sem 2'];
          if (year === 'PG SY' || year === 'PG Second Year') return ['PG Sem 3', 'PG Sem 4'];
        }
        return [];
      };

      const yearOrder = getYearOrder(stream);
      
      yearOrder.forEach(year => {
        if (!groupedSubjects[stream][year]) return;
        
        const semesterOrder = getSemesterOrder(year, stream);
        
        semesterOrder.forEach(semester => {
          if (!groupedSubjects[stream][year][semester]) return;
          
          // Year-Semester subheader
          const getYearLabel = (year) => {
            if (year === 'FY') return 'First Year';
            if (year === 'SY') return 'Second Year';
            if (year === 'TY') return 'Third Year';
            if (year === 'Fourth Year') return 'Fourth Year';
            if (year === 'PG FY') return 'PG First Year';
            if (year === 'PG SY') return 'PG Second Year';
            if (year === 'PG First Year') return 'PG First Year'; // Handle full format
            if (year === 'PG Second Year') return 'PG Second Year'; // Handle full format
            if (year === 'Year 1') return 'Year 1'; // Legacy format
            if (year === 'Year 2') return 'Year 2'; // Legacy format
            return year;
          };
          
          const yearSemesterLabel = `${getYearLabel(year)} - ${semester}`;
          
          elements.push(
            <div key={`header-${stream}-${year}-${semester}`} className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50">
              {yearSemesterLabel}
            </div>
          );

          // Subjects in this group
          groupedSubjects[stream][year][semester].forEach(subject => {
            // Check if subject is marked as completed for selected batch
            const selectedBatch = batches.find(b => b.id === formData.batch_id);
            const isCompleted = selectedBatch?.completed_subjects?.includes(subject.id);
            
            elements.push(
              <SelectItem key={subject.id} value={subject.id} className="pl-6">
                <div className="flex items-center justify-between w-full">
                  <span className={isCompleted ? 'text-green-600 line-through' : ''}>
                    {subject.name}
                    {isCompleted && <span className="ml-2 text-xs">✅ Completed</span>}
                  </span>
                  {subject.total_classes && (
                    <span className="text-xs text-gray-500 ml-2">({subject.total_classes} classes)</span>
                  )}
                </div>
              </SelectItem>
            );
          });
        });
      });
    });

    return elements;
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Unknown Subject';
  };

  const getFilteredTeachers = () => {
    if (!formData.subject_id) return teachers;
    
    // For teachers: only show themselves
    if (isTeacher() && currentTeacher) {
      return currentTeacher.subjects && currentTeacher.subjects.includes(formData.subject_id) 
        ? [currentTeacher] 
        : [];
    }
    
    // For admins: show all qualified teachers
    return teachers.filter(teacher => 
      teacher.subjects && teacher.subjects.includes(formData.subject_id)
    );
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? teacher.name : 'Unknown Teacher';
  };

  const getClassroomName = (classroomId) => {
    const classroom = classrooms.find(c => c.id === classroomId);
    return classroom ? classroom.name : 'Unknown Classroom';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <Zap className="w-8 h-8 mr-3 text-blue-600" />
            Smart Timetable Builder
          </h1>
          <p className="text-gray-600">
            {isTeacher() 
              ? 'Create course schedules for your assigned subjects'
              : 'Create course schedules that automatically generate recurring sessions'
            }
          </p>
          {isTeacher() && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Teacher Mode:</strong> You can only schedule subjects assigned to you and cannot select other faculty members.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                Course Schedule Builder
              </CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Step 1: Batch Selection */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">1</span>
                    Select Batch
                  </h3>
                  
                  <Select value={formData.batch_id} onValueChange={(value) => setFormData({...formData, batch_id: value, subject_id: undefined})}>
                    <SelectTrigger data-testid="course-batch-select">
                      <SelectValue placeholder="Choose batch" />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map(batch => (
                        <SelectItem key={batch.id} value={batch.id}>
                          {batch.name}
                          {batch.stream && ` (${batch.stream})`}
                          {batch.year && ` - ${batch.year}`}
                          {batch.semester && ` - ${batch.semester}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Show selected batch details */}
                  {getSelectedBatch() && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm text-blue-800">
                        <strong>Selected Batch Details:</strong>
                      </div>
                      <div className="text-xs text-blue-600 mt-1 space-y-1">
                        {getSelectedBatch().stream && (
                          <div>Stream: <span className="font-medium">{getSelectedBatch().stream}</span></div>
                        )}
                        {getSelectedBatch().year && (
                          <div>Year: <span className="font-medium">{getSelectedBatch().year}</span></div>
                        )}
                        {getSelectedBatch().semester && (
                          <div>Semester: <span className="font-medium">{getSelectedBatch().semester}</span></div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Step 2: Subject & Faculty */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">2</span>
                      Subject
                    </h3>
                    
                    <Select 
                      value={formData.subject_id} 
                      onValueChange={(value) => {
                        const updatedData = {...formData, subject_id: value};
                        
                        // For teachers: automatically set themselves as teacher if qualified
                        if (isTeacher() && currentTeacher && currentTeacher.subjects && currentTeacher.subjects.includes(value)) {
                          updatedData.teacher_id = currentTeacher.id;
                        }
                        
                        setFormData(updatedData);
                      }}
                      disabled={!formData.batch_id}
                    >
                      <SelectTrigger data-testid="course-subject-select">
                        <SelectValue placeholder={
                          !formData.batch_id 
                            ? "Select batch first" 
                            : getFilteredSubjects.length === 0 
                              ? "No subjects available for selected batch"
                              : "Choose subject"
                        } />
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {getFilteredSubjects.length > 0 ? renderSubjectGroups() : (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No subjects available for selected batch
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    
                    {/* Show filtered subjects info */}
                    {formData.batch_id && (
                      <div className="mt-2 text-xs text-gray-600">
                        {getFilteredSubjects.length > 0 ? (
                          <span className="text-green-600">
                            ✓ {getFilteredSubjects.length} subject(s) available for selected batch
                          </span>
                        ) : (
                          <span className="text-orange-600">
                            ⚠ No subjects found for this batch's stream/semester combination
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">3</span>
                      Faculty
                    </h3>
                    
                    {isTeacher() ? (
                      // For teachers: show read-only their own name
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-center">
                          <GraduationCap className="w-5 h-5 text-blue-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">
                              {currentTeacher ? currentTeacher.name : user?.name || 'You'}
                            </p>
                            <p className="text-xs text-blue-600">Assigned Faculty</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // For admins: show teacher selection
                      <Select 
                        value={formData.teacher_id} 
                        onValueChange={(value) => setFormData({...formData, teacher_id: value})}
                        disabled={!formData.subject_id}
                      >
                        <SelectTrigger data-testid="course-teacher-select">
                          <SelectValue placeholder={
                            !formData.subject_id 
                              ? "Select subject first" 
                              : getFilteredTeachers().length === 0 
                                ? "No qualified teachers for this subject"
                                : "Choose faculty"
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {getFilteredTeachers().map(teacher => (
                            <SelectItem key={teacher.id} value={teacher.id}>
                              {teacher.name}
                              <span className="text-green-600 ml-2 text-xs">✓ Qualified</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Show teacher qualification info */}
                    {formData.subject_id && !isTeacher() && (
                      <div className="mt-2 text-xs text-gray-600">
                        {getFilteredTeachers().length > 0 ? (
                          <span className="text-green-600">
                            ✓ {getFilteredTeachers().length} qualified teacher(s) available
                          </span>
                        ) : (
                          <span className="text-orange-600">
                            ⚠ No teachers assigned to this subject yet
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Show teacher-specific messaging */}
                    {isTeacher() && formData.subject_id && (
                      <div className="mt-2 text-xs text-gray-600">
                        {currentTeacher && currentTeacher.subjects && currentTeacher.subjects.includes(formData.subject_id) ? (
                          <span className="text-green-600">
                            ✓ You are qualified to teach this subject
                          </span>
                        ) : (
                          <span className="text-orange-600">
                            ⚠ You are not assigned to this subject
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 3: Classroom & Classes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">4</span>
                      Classroom
                    </h3>
                    
                    <Select value={formData.classroom_id} onValueChange={(value) => setFormData({...formData, classroom_id: value})}>
                      <SelectTrigger data-testid="course-classroom-select">
                        <SelectValue placeholder="Choose classroom" />
                      </SelectTrigger>
                      <SelectContent>
                        {classrooms.map(classroom => (
                          <SelectItem key={classroom.id} value={classroom.id}>
                            {classroom.name}
                            {classroom.location && ` - ${classroom.location}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Total Classes Required
                    </h3>
                    
                    <div className="space-y-2">
                      <Input
                        type="number"
                        value={formData.total_classes}
                        onChange={(e) => setFormData({...formData, total_classes: e.target.value})}
                        placeholder="e.g., 15"
                        min="1"
                        max="100"
                        className="form-input"
                        data-testid="course-total-classes"
                      />
                      
                      {/* Show subject's default class count if available */}
                      {formData.subject_id && (() => {
                        const selectedSubject = subjects.find(s => s.id === formData.subject_id);
                        return selectedSubject?.total_classes && (
                          <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                            💡 Suggested: {selectedSubject.total_classes} classes (from subject settings)
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, total_classes: selectedSubject.total_classes.toString()})}
                              className="ml-2 text-blue-700 underline hover:text-blue-800"
                            >
                              Use this
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Step 4: Schedule */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">5</span>
                    Schedule Configuration
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Days of the Week
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {DAYS.map(day => (
                          <label key={day.value} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={formData.days_of_week.includes(day.value)}
                              onChange={() => handleDayToggle(day.value)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              data-testid={`day-${day.value.toLowerCase()}`}
                            />
                            <span className="text-sm text-gray-700">{day.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Time Slot
                      </label>
                      <Select value={formData.time_slot} onValueChange={(value) => setFormData({...formData, time_slot: value})}>
                        <SelectTrigger data-testid="course-time-slot-select">
                          <SelectValue placeholder="Choose time slot" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.length > 0 ? timeSlots.map(slot => (
                            <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                          )) : (
                            <div className="px-3 py-2 text-sm text-gray-500">No time slots available</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Start Date
                      </label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                        className="form-input"
                        data-testid="course-start-date"
                      />
                    </div>

                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePreview}
                        className="w-full"
                        data-testid="preview-schedule-btn"
                        disabled={!formData.total_classes || !formData.days_of_week.length || !formData.start_date}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Preview Schedule
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex space-x-4">
                  <Button
                    type="submit"
                    className="flex-1 btn-primary"
                    data-testid="create-course-btn"
                    disabled={
                      !formData.batch_id || 
                      !formData.subject_id || 
                      !formData.teacher_id || 
                      !formData.classroom_id || 
                      !formData.total_classes || 
                      !formData.days_of_week.length || 
                      !formData.time_slot || 
                      !formData.start_date ||
                      (isTeacher() && currentTeacher && (!currentTeacher.subjects || !currentTeacher.subjects.includes(formData.subject_id)))
                    }
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Create Course Schedule
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Preview Section */}
        <div className="space-y-6">
          {previewData && (
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="text-lg text-green-800">
                  Schedule Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Classes:</span>
                    <Badge variant="outline">{previewData.totalClasses}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Classes per Week:</span>
                    <Badge variant="secondary">{previewData.sessionsPerWeek}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Approximate Duration:</span>
                    <Badge className="bg-blue-100 text-blue-800">{previewData.approximateWeeks} weeks</Badge>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Days:</h4>
                  <div className="flex flex-wrap gap-1">
                    {formData.days_of_week.map(day => (
                      <Badge key={day} variant="outline" className="text-xs">
                        {day}
                      </Badge>
                    ))}
                  </div>
                </div>

                {formData.time_slot && (
                  <div className="pt-2">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Time Slot:</h4>
                    <Badge className="bg-green-100 text-green-800">{formData.time_slot}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Course Summary */}
          {(formData.batch_id || formData.subject_id || formData.teacher_id || formData.classroom_id) && (
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="text-lg text-blue-800">
                  Course Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {formData.batch_id && (
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-900">{getBatchName(formData.batch_id)}</span>
                  </div>
                )}
                {formData.subject_id && (
                  <div className="flex items-center space-x-2">
                    <BookOpen className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-900">{getSubjectName(formData.subject_id)}</span>
                  </div>
                )}
                {formData.teacher_id && (
                  <div className="flex items-center space-x-2">
                    <GraduationCap className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-900">{getTeacherName(formData.teacher_id)}</span>
                  </div>
                )}
                {formData.classroom_id && (
                  <div className="flex items-center space-x-2">
                    <Building className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-900">{getClassroomName(formData.classroom_id)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Conflicts Warning */}
          {conflicts.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-lg text-red-800 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Conflicts Detected
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {conflicts.map((conflict, index) => (
                    <li key={index} className="text-sm text-red-700">
                      • {conflict}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Conflict Confirmation Dialog */}
      <Dialog open={conflictDialog.open} onOpenChange={handleCancelConflict}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center text-orange-800">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Scheduling Conflict Detected
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                {conflictDialog.message}
              </p>
            </div>
            
            <div className="text-sm text-gray-600">
              <p>
                <strong>Do you want to proceed anyway?</strong>
              </p>
              <p className="mt-2">
                Creating overlapping assignments may cause scheduling conflicts. 
                This might be acceptable for special sessions, makeup classes, or temporary arrangements.
              </p>
            </div>
          </div>

          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleCancelConflict}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmConflict}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Proceed Anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SmartTimetableBuilder;